<template>
  <h4 class="accordion-header" id="headingThree">
    <button
      type="button"
      class="accordion-button collapsed d-flex flex-row justify-content-between gap-2 stm-btn-accordion-venmo"
      data-bs-toggle="collapse"
      data-bs-target="#accordion-venmo"
      aria-expanded="false"
      aria-controls="accordion-venmo"
    >
      <div class="d-flex justify-content-between w-100">
        <div>Venmo</div>
        <img alt="Venmo supported" class="icon" :src="venmoSvg" />
      </div>
    </button>
  </h4>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <div
    id="accordion-venmo"
    class="accordion-collapse collapse"
    aria-labelledby="headingOne"
    data-bs-parent="#paymentAccordion"
    @click="$emit('showVuelidate')"
  >
    <PaymentErrorAlert payment-method-name="venmo" :method-error="panel.methodError" :partial-success="panel.partialSuccess" />
    <div class="accordion-body card-element justify-content-center">
      <button
        type="button"
        id="venmo-button"
        class="accordion-body btn btn-lg btn-block w-100 venmo-button-color stm-btn-venmo-start"
        style="max-height: 50%"
        @click="handleVenmoClick"
        :disabled="disableButton || paymentState.processing"
      >
        <span v-if="paymentState.processing" class="text-white">Processing with Venmo... </span>
        <img class="h-50 venmo-logo-color" :src="venmoLogoSvg" alt="Venmo full logo" aria-label="venmo" v-else />
      </button>
      <PaymentSecurityBadges />
    </div>
  </div>
</template>
<script setup>
import { onMounted, watch } from 'vue';
import { capturePaymentError } from '@/services/sentry';
import { useCheckoutStore } from '@/stores/checkout';
import PaymentErrorAlert from '@/components/PaymentErrorAlert.vue';
import PaymentSecurityBadges from '@/components/PaymentSecurityBadges.vue';

import venmoSvg from '@/assets/svg/venmo.svg';
import venmoLogoSvg from '@/assets/svg/venmo-logo.svg';

const checkoutStore = useCheckoutStore();
const { checkoutData, paymentState, handlePanelToken } = checkoutStore;
const panel = checkoutStore.panelState.venmo;

const props = defineProps({
  disableButton: Boolean,
});

const emit = defineEmits(['paymentProcessing', 'paymentFailed', 'showVuelidate']);

let venmo;

const handleVenmoClick = async () => {
  try {
    await venmo.start();
  } catch (err) { // this is most typically a network issue
    panel.methodError = err?.message || (typeof err === 'string' ? err : 'Venmo checkout failed');
  }
};

const initializeVenmo = () => {
  venmo = recurly.Venmo({
    form: document.getElementById('form'),
    braintree: {
      clientAuthorization: import.meta.env.VITE_BRAINTREE_KEY,
      webAuthentication: true,
    },
    display: {
      amount: checkoutData.totalPrice,
      currency: 'USD',
      displayName: 'Speedify',
      locale: 'en_US',
    },
  });

  venmo.on('error', err => {
    const code = err?.cause?.code || err?.code;
    // don't display if the user simply canceled auth
    if (code === "VENMO_TOKENIZATION_CANCELED_BY_MERCHANT"
      || code === "FRAME_SERVICE_FRAME_CLOSED"
      || err?.message?.includes?.('venmo-load-error')) {
      return;
    }
    capturePaymentError('venmo_tokenize', err);
    panel.methodError = err?.message || 'Venmo was not able to process the transaction. Please use a different payment method or try again later.';
  });

  venmo.on('token', async token => {
    emit('paymentProcessing');
    await handlePanelToken('venmo', token);
  });
};

watch(() => paymentState.purchaseFailed || paymentState.purchasePartiallySucceeded, (hasFailed) => {
  if (hasFailed) {
    emit('paymentFailed');
  }
});

onMounted(() => {
  if (!window.recurly || !window.braintree?.venmo) {
    if (window.braintree && !window.braintree.venmo) {
      panel.methodError = 'Venmo is not available right now. Please try another payment method.';
    }
    return;
  }

  try {
    initializeVenmo();
  } catch (err) {
    panel.methodError = 'Failed to initialize Venmo. Please try another payment method.';
  }
});
</script>
